// Input validation service for vehicle diagnostic responses

interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

// Keywords that indicate non-vehicle related content
const NON_VEHICLE_KEYWORDS = [
  // Personal/off-topic
  'weather', 'food', 'movie', 'music', 'sports', 'politics', 'religion', 'family', 'work', 'job',
  'school', 'vacation', 'travel', 'shopping', 'money', 'health', 'medical', 'doctor', 'hospital',
  'love', 'relationship', 'dating', 'marriage', 'children', 'kids', 'baby', 'pets', 'dog', 'cat',
  
  // Technology (non-automotive)
  'computer', 'phone', 'laptop', 'internet', 'website', 'app', 'software', 'programming', 'code',
  'gaming', 'video game', 'console', 'streaming', 'netflix', 'youtube', 'social media',
  
  // General topics
  'joke', 'funny', 'story', 'book', 'news', 'entertainment', 'celebrity', 'fashion', 'clothes',
  'house', 'home', 'garden', 'cooking', 'recipe', 'restaurant', 'party', 'birthday', 'holiday'
];

// Keywords that indicate unrealistic/impossible symptoms
const UNREALISTIC_SYMPTOMS = [
  'flying', 'teleporting', 'magic', 'supernatural', 'alien', 'ufo', 'ghost', 'spirit',
  'time travel', 'dimension', 'parallel universe', 'robot', 'ai consciousness',
  'car talks', 'car thinks', 'car dreams', 'car feels', 'car remembers',
  'car is alive', 'car has emotions', 'car is angry', 'car is sad', 'car is happy'
];

// Keywords that indicate valid vehicle-related content
const VEHICLE_KEYWORDS = [
  // Engine related
  'engine', 'motor', 'start', 'starting', 'crank', 'cranking', 'ignition', 'spark', 'fuel', 'gas',
  'oil', 'coolant', 'temperature', 'overheat', 'smoke', 'exhaust', 'emission', 'idle', 'rpm',
  'power', 'acceleration', 'throttle', 'injector', 'carburetor', 'turbo', 'supercharger',
  
  // Electrical
  'battery', 'alternator', 'starter', 'electrical', 'wiring', 'fuse', 'relay', 'sensor',
  'light', 'headlight', 'taillight', 'brake light', 'turn signal', 'dash', 'gauge',
  'check engine', 'warning light', 'error code', 'dtc', 'obd', 'diagnostic',
  
  // Transmission
  'transmission', 'gear', 'shift', 'shifting', 'clutch', 'automatic', 'manual', 'cvt',
  'torque converter', 'differential', 'axle', 'driveshaft', 'transfer case',
  
  // Brakes
  'brake', 'braking', 'pad', 'rotor', 'caliper', 'line', 'fluid', 'abs', 'parking brake',
  'pedal', 'squeak', 'grind', 'vibration', 'pulsation',
  
  // Suspension/Steering
  'suspension', 'shock', 'strut', 'spring', 'bushing', 'bearing', 'wheel', 'tire', 'alignment',
  'steering', 'steer', 'wheel', 'tie rod', 'ball joint', 'control arm', 'sway bar',
  
  // Body/Interior
  'door', 'window', 'mirror', 'seat', 'airbag', 'seatbelt', 'radio', 'ac', 'heater', 'fan',
  'wiper', 'horn', 'lock', 'key', 'remote', 'alarm', 'security',
  
  // Performance/Symptoms
  'noise', 'sound', 'click', 'knock', 'tick', 'rattle', 'squeal', 'whine', 'grind', 'hum',
  'vibration', 'shake', 'rough', 'smooth', 'hesitation', 'stall', 'stalling', 'misfire',
  'backfire', 'ping', 'detonation', 'knocking', 'pulling', 'drifting', 'wandering',
  
  // Fluids
  'leak', 'leaking', 'drip', 'dripping', 'fluid', 'water', 'antifreeze', 'coolant',
  'transmission fluid', 'brake fluid', 'power steering', 'hydraulic',
  
  // General vehicle terms
  'car', 'vehicle', 'auto', 'automobile', 'truck', 'suv', 'van', 'sedan', 'coupe',
  'convertible', 'hatchback', 'wagon', 'pickup', 'drive', 'driving', 'road', 'highway',
  'park', 'parking', 'reverse', 'forward', 'neutral', 'park', 'drive', 'low'
];

/**
 * Validates if the user input is relevant to vehicle diagnostics
 */
export const validateVehicleDiagnosticInput = (input: string): ValidationResult => {
  const lowerInput = input.toLowerCase().trim();
  
  // Check for empty or very short input
  if (lowerInput.length < 2) {
    return {
      isValid: false,
      errorMessage: "Please provide a more detailed response about your vehicle's symptoms."
    };
  }
  
  // Check for non-vehicle related keywords
  const hasNonVehicleKeywords = NON_VEHICLE_KEYWORDS.some(keyword => 
    lowerInput.includes(keyword.toLowerCase())
  );
  
  if (hasNonVehicleKeywords) {
    return {
      isValid: false,
      errorMessage: "This information doesn't seem related to vehicle diagnostics. Please focus on describing your vehicle's symptoms, sounds, or behavior."
    };
  }
  
  // Check for unrealistic symptoms
  const hasUnrealisticSymptoms = UNREALISTIC_SYMPTOMS.some(keyword => 
    lowerInput.includes(keyword.toLowerCase())
  );
  
  if (hasUnrealisticSymptoms) {
    return {
      isValid: false,
      errorMessage: "The symptoms you've described don't seem realistic for a vehicle. Please describe actual mechanical symptoms you're experiencing."
    };
  }
  
  // Check if input contains vehicle-related keywords
  const hasVehicleKeywords = VEHICLE_KEYWORDS.some(keyword => 
    lowerInput.includes(keyword.toLowerCase())
  );
  
  if (!hasVehicleKeywords) {
    return {
      isValid: false,
      errorMessage: "Please describe symptoms related to your vehicle's mechanical, electrical, or performance issues."
    };
  }
  
  // Additional checks for common off-topic patterns
  if (lowerInput.includes('how are you') || 
      lowerInput.includes('what is your name') ||
      lowerInput.includes('tell me about yourself') ||
      lowerInput.includes('what can you do') ||
      lowerInput.includes('help me with') && !lowerInput.includes('car') && !lowerInput.includes('vehicle')) {
    return {
      isValid: false,
      errorMessage: "I'm here to help diagnose vehicle problems. Please describe the symptoms or issues you're experiencing with your vehicle."
    };
  }
  
  return { isValid: true };
};

/**
 * Validates if the input is a reasonable response to the current question
 */
export const validateQuestionResponse = (input: string, questionText: string): ValidationResult => {
  const baseValidation = validateVehicleDiagnosticInput(input);
  if (!baseValidation.isValid) {
    return baseValidation;
  }
  
  const lowerInput = input.toLowerCase();
  const lowerQuestion = questionText.toLowerCase();
  
  // Check if the response seems to answer the question
  if (lowerQuestion.includes('noise') && !lowerInput.includes('noise') && !lowerInput.includes('sound') && !lowerInput.includes('quiet')) {
    return {
      isValid: false,
      errorMessage: "The question is asking about noise or sounds. Please describe what you hear (or don't hear) from your vehicle."
    };
  }
  
  if (lowerQuestion.includes('when') && !lowerInput.includes('when') && !lowerInput.includes('time') && !lowerInput.includes('during')) {
    return {
      isValid: false,
      errorMessage: "The question is asking about timing. Please describe when the problem occurs (e.g., 'when starting', 'when driving', 'when cold')."
    };
  }
  
  if (lowerQuestion.includes('color') && !lowerInput.includes('color') && !lowerInput.includes('white') && !lowerInput.includes('black') && !lowerInput.includes('blue') && !lowerInput.includes('gray') && !lowerInput.includes('none')) {
    return {
      isValid: false,
      errorMessage: "The question is asking about color. Please describe the color you see (e.g., 'white smoke', 'black smoke', 'no smoke')."
    };
  }
  
  return { isValid: true };
};
